import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, {useContext, useEffect, useRef, useState} from "react";
import ValidatedTextField from "../Inputs/ValidatedTextField";
import {alphaNumericValidator, checkAllValid, numberValidator} from "../../Helpers/ValidationHelpers";
import equal from "fast-deep-equal";
import Divider from "@mui/material/Divider";
import {produce} from "immer";
import ProjectDetailCheckbox from "../Projects/ProjectDetailCheckbox";
import {useDispatch} from "react-redux";
import {putQuoteTemplate} from "../../redux/ProjectsSlice";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import {IsOnboardedContext} from "../../Contexts/IsOnboardedContext";
import {enqueueSnackbar} from "notistack";
import LoadingOverlay from "../Loading/LoadingOverlay";

const EditTemplateDialog = ({openDialog, handleClose, templateToEdit, existingFields, dispatcher }) => {
    const productType = templateToEdit?.productGroupId ? 'group' : 'item';
    const template = templateToEdit?.template;
    const formValid = useRef({
        name: true,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = dispatcher ?? useDispatch();
    const context = useContext(IsOnboardedContext);

    useEffect(() => {
        if (template?.categoryItems) {
            template.categoryItems.forEach((catItem) => {
                formValid.current[`${catItem.productCategoryId}-${catItem.productItemId}`] = {};
                catItem.fields.forEach((field) => {
                    formValid.current[`${catItem.productCategoryId}-${catItem.productItemId}`][field.fieldId] = true;
                });
            });
        }

        if (template?.quoteDetailFieldValues) {
            template.quoteDetailFieldValues.forEach((item) => {
                item.fields.forEach((field) => {
                    formValid.current[field.fieldId] = true;
                });
            });
        }
    }, [])
    
    const [activeForm, setActiveForm] = useState({
        ...templateToEdit
    });
    
    const handleSubmit = () => {
        if (equal(templateToEdit, activeForm)) {
            handleClose();
            return;
        }

        if (checkAllValid(formValid.current)) {
            setIsSubmitting(true);
            dispatch(
                putQuoteTemplate(
                    TrimObjectStrings({
                        id: templateToEdit.id,
                        payload: {
                            ...activeForm,
                            lastModifiedBy: context.userName,
                        },
                    })
                )
            ).unwrap()
                .then(() => {
                    setIsSubmitting(false);

                    enqueueSnackbar("Parameter successfully updated!", {
                        variant: "success",
                        autoHideDuration: 3000,
                    });

                    handleClose();
                })
            .catch(() => {
                const errorMessage = "Failed to update template. Please try again.";
                console.error(errorMessage);
                setIsSubmitting(false);
                enqueueSnackbar(errorMessage, {
                    variant: "error",
                    autoHideDuration: 3000,
                });
                handleClose();
            });
        }
    }

    const getItemFieldNames = () => {
        const extractFields = (items) =>
            items?.map((item) => ({
                productItemName: item.productItemName,
                fields: item.fields.map((field) => {
                    const fieldData = existingFields.find((x) => x.id === field.fieldId);
                    return {
                        name: fieldData?.name,
                        id: fieldData?.id,
                        uom: fieldData?.uom,
                        displayName: fieldData?.displayName,
                        enumOptions: fieldData?.enumOptions,
                        type: fieldData?.type,
                    };
                }),
            }));

        return extractFields(template?.quoteDetailFieldValues || []);
    };
    
    const getGroupFieldNames = () => {
        const intersectFieldIds = (items) => {
            // Extract fields for each item and find their intersection by ID
            const allFieldData = items.map((item) =>
                item.fields.map((field) => ({
                    fieldId: field.fieldId,
                    value: field.value,
                    productCategoryDetails: items.map((item) => ({
                        productItemId: item.productItemId,
                        productCategoryId: item.productCategoryId,
                    })),
                }))
            );

            // Reduce to find common field IDs and ensure they have the same value across items
            return allFieldData.reduce((commonFields, currentFields) =>
                commonFields.filter((commonField) =>
                    currentFields.some(
                        (field) =>
                            field.fieldId === commonField.fieldId && field.value === commonField.value
                    )
                )
            );
        };

        const extractFields = (items, commonFieldIds) =>
            items?.map((item) => ({
                productItemId: item.productItemId,
                productCategoryId: item.productCategoryId,
                productItemName: item.productItemName,
                // Exclude fields that are part of commonFieldIds
                fields: item.fields.filter(
                        (field) =>
                            !commonFieldIds.some(
                                (commonField) => commonField.fieldId === field.fieldId
                            )
                    )
                    .map((field) => {
                        const fieldData = existingFields.find(
                            (x) => x.id === field.fieldId
                        );
                        return {
                            name: fieldData?.name,
                            id: fieldData?.id,
                            uom: fieldData?.uom,
                            displayName: fieldData?.displayName,
                            enumOptions: fieldData?.enumOptions,
                            type: fieldData?.type,
                            value: field.value,
                            productItemId: item.productItemId, 
                            productCategoryId: item.productCategoryId,
                        };
                    }),
            }));

        const findCommonFields = (items) => {
            const commonFieldData = intersectFieldIds(items);
            // Map common fields to their full data
            return commonFieldData.map(({ fieldId, value, productCategoryDetails }) => {
                const fieldData = existingFields.find((x) => x.id === fieldId);
                return {
                    name: fieldData?.name,
                    id: fieldData?.id,
                    uom: fieldData?.uom,
                    displayName: fieldData?.displayName,
                    enumOptions: fieldData?.enumOptions,
                    type: fieldData?.type,
                    value,
                    productCategoryDetails
                };
            });
        };

        const items = template?.categoryItems || [];
        const commonFieldIds = intersectFieldIds(items);
        return {
            items: extractFields(items, commonFieldIds),
            commonFields: findCommonFields(items),
        };
    }
    
    const handleItemSetValue = (newValue, id) => {
        setActiveForm(produce(activeForm, draft => {
            const fieldToUpdate = draft.template.quoteDetailFieldValues[0]?.fields?.find((x) => x.fieldId === id);
            if (fieldToUpdate) {
                fieldToUpdate.value = newValue;
            }
        }))
    }
    
    const handleGroupSetValue = (newValue, field) => {
        setActiveForm(produce(activeForm, draft => {
            if (field.productCategoryDetails) {
                const categoryDetailsSet = new Set(
                    field.productCategoryDetails.map(
                        (detail) => `${detail.productCategoryId}-${detail.productItemId}`
                    )
                );

                draft.template.categoryItems.forEach((item) => {
                    if (categoryDetailsSet.has(`${item.productCategoryId}-${item.productItemId}`)) {
                        const fieldToUpdate = item.fields.find((x) => x.fieldId === field.id);
                        if (fieldToUpdate) {
                            fieldToUpdate.value = newValue;
                        }
                    }
                });

                return;
            }
            
            const fieldToUpdate = draft.template.categoryItems
                .find(item => item.productCategoryId === field.productCategoryId 
                    && item.productItemId === field.productItemId)
                ?.fields?.find((x) => x.fieldId === field.id);
            if (fieldToUpdate) {
                fieldToUpdate.value = newValue;
            }
        }))
    }

    const itemsWithFields = productType === "item" ? getItemFieldNames() : getGroupFieldNames();

    const handleItemFieldRender = (field, index, isProductGroupField) => {
        const commonProps = {
            id: field.id,
            label: field.displayName,
            helperText: null,
            removeHelperTextPadding: true,
            onChange: (isValid) => {
                if (isProductGroupField) {
                    if (field.productCategoryDetails?.length) {
                        field.productCategoryDetails.forEach((item) => {
                            const key = `${item.productCategoryId}-${item.productItemId}`;
                            formValid.current[key] = formValid.current[key] || {};
                            formValid.current[key][field.id] = isValid;
                        });
                    } else {
                        const key = `${field.productCategoryId}-${field.productItemId}`;
                        formValid.current[key] = formValid.current[key] || {};
                        formValid.current[key][field.id] = isValid;
                    }
                } else {
                    formValid.current[field.id] = isValid;
                }
            },
            customFormSetter: (value) => isProductGroupField 
                ? handleGroupSetValue(value, field) 
                : handleItemSetValue(value, field.id),
            initialValue: isProductGroupField 
                ? templateToEdit?.template?.categoryItems.find(
                    item => item.productCategoryId === (field.productCategoryId ?? field.productCategoryDetails[0].productCategoryId)
                    && item.productItemId === (field.productItemId ?? field.productCategoryDetails[0].productItemId))
                ?.fields?.find((x) => x.fieldId === field.id)?.value
            : templateToEdit?.template?.quoteDetailFieldValues[0]
                    ?.fields?.find((x) => x.fieldId === field.id)?.value,
            size: "small",
        };

        switch (field.type) {
            case "Text":
            case 1:
                return (
                    <Box sx={{ my: 2 }} key={index}>
                        <ValidatedTextField {...commonProps} validator={alphaNumericValidator} />
                    </Box>
                );
            case "Number":
            case 2:
                return (
                    <Box sx={{ my: 2 }} key={index}>
                        <ValidatedTextField
                            {...commonProps}
                            validator={numberValidator}
                            infoAdornment={<InputAdornment position="end">{field.uom}</InputAdornment>}
                        />
                    </Box>
                );
            case "Enumeration":
            case 3:
                return (
                    <Box sx={{ my: 2 }} key={index}>
                        <ValidatedTextField {...commonProps} options={field.enumOptions} />
                    </Box>
                );
            case "Boolean":
            case 4:
                return (
                    <Box sx={{ my: 0 }} key={index}>
                        <ProjectDetailCheckbox
                            field={field}
                            customFormSetter={(value) => isProductGroupField
                                ? handleGroupSetValue(value ? "YES" : "NO", field)
                                : handleItemSetValue(value ? "YES" : "NO", field.id)
                            }
                            
                            draftQuoteDetail={activeForm.template}
                            productItemId={isProductGroupField
                                ? field.productItemId ?? field.productCategoryDetails[0].productItemId 
                                : templateToEdit?.productItem.id}
                        />
                    </Box>
                );
            default:
                return null;
        }
    };
    
    return <>
        {isSubmitting && <LoadingOverlay />}
        <Dialog 
            open={openDialog}
            onClose={handleClose}
        >
            <Box
                sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 3,
                    py: 1,
                }}
            >
                <Typography
                    variant='h6'
                    sx={{ color: (theme) => theme.palette.primary.contrastText, pr: 3}}
                >
                    {(() => {
                       return `Product: ${templateToEdit?.productGroup?.name ?? templateToEdit?.productItem?.name}`
                    })()}
                </Typography>
                <IconButton
                    aria-label='close'
                    onClick={handleClose}
                    sx={{
                        color: (theme) => theme.palette.primary.contrastText,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogTitle>{`Edit template "${templateToEdit?.name}"`}</DialogTitle>
            <DialogContent>
                <Box sx={{ my: 1}}>
                    <ValidatedTextField
                        id={'name'}
                        label={'Name'}
                        validator={alphaNumericValidator}
                        helperText={"The name of the template"}
                        onChange={(isValid) => (formValid.current.name = isValid)}
                        setActiveForm={setActiveForm}
                        initialValue={templateToEdit?.name}
                        fieldPath={["name"]}
                        required
                        infoAdornment={null}
                    />
                </Box>
                <Box sx={{ my: 1}}>
                    <Typography variant='h5'>{`Parameters`}</Typography>
                    <Divider sx={{my: 1}}/>
                    {itemsWithFields.length === 1
                        ? itemsWithFields[0].fields.map(
                            (field, index) => handleItemFieldRender(field, index))
                        : (
                            <>
                                <Typography variant="h6">Common</Typography>
                                {itemsWithFields.commonFields.map(
                                    (field, index) => handleItemFieldRender(field, index, true))
                                }
                                {itemsWithFields.items.map((item) => (
                                    item.fields.length > 0 && (
                                        <Box key={item.productItemName}>
                                            <Typography variant="h6">{item.productItemName}</Typography>
                                            {item.fields.map((field, index) => (
                                                handleItemFieldRender(field, index, true))
                                            )}
                                        </Box>
                                    )
                                ))}
                            </>
                        )
                    }
                </Box>
                
            </DialogContent>
            <DialogActions sx={{ mb: 1 }}>
                <Button onClick={handleClose}>Back</Button>
                <Button
                    onClick={handleSubmit}
                    variant={"contained"}
                    disabled={!checkAllValid(formValid.current)}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    </>
}

export default EditTemplateDialog;